import { unwrapResult } from "@reduxjs/toolkit";

export const createCustomDispatch = (dispatch) => {
  return async (actionCreator) => {
    try {
      const actionResult = await dispatch(actionCreator);
      const result = unwrapResult(actionResult);

      return result;
    } catch (error) {
      // Handle 401 errors silently - modal is already shown by callApi
      // unwrapResult wraps rejected actions, so check error.payload for the actual error value
      const errorValue = error?.payload || error;
      const is401 = 
        error === 401 || 
        errorValue === 401 ||
        error?.status === 401 || 
        errorValue?.status === 401 ||
        error?.error === 401 ||
        errorValue?.error === 401 ||
        (typeof errorValue === 'string' && errorValue.includes('401')) ||
        (typeof error === 'string' && error.includes('401'));
      
      if (is401) {
        // Don't throw 401 errors - modal is already shown
        console.warn('401 Unauthorized - session expired modal shown');
        return { error: 'Unauthorized', status: 401 };
      }
      
      // Extract error message for better error handling
      let errorMessage = '';
      if (error && error.error) {
        errorMessage = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      } else if (error && error.message) {
        errorMessage = error.message;
      } else if (error && error.payload) {
        const payload = error.payload;
        errorMessage = payload.error || payload.message || (typeof payload === 'string' ? payload : JSON.stringify(payload));
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object') {
        // Try to stringify, but avoid "[object Object]"
        try {
          const stringified = JSON.stringify(error);
          errorMessage = stringified !== '{}' ? stringified : 'Server error';
        } catch {
          errorMessage = 'Server error';
        }
      } else {
        errorMessage = 'Server error';
      }
      
      // Check if error is "Server error" or "[object Object]" - these should be handled silently
      const isGenericError = 
        errorMessage === 'Server error' || 
        errorMessage === '[object Object]' ||
        errorMessage.includes('Server error') ||
        (errorMessage === '{}' && error && typeof error === 'object');
      
      // For backward compatibility, still throw other errors
      // This maintains existing try-catch behavior across the project
      // But don't throw generic errors that are not handled - return error object instead
      if (isGenericError) {
        // Return error object instead of throwing to prevent uncaught errors
        return { error: 'Server error', originalError: error };
      }
      
      // Only log non-401 errors in development mode to reduce console noise
      if (process.env.NODE_ENV === 'development') {
        console.warn('CustomDispatch throwing error for backward compatibility:', errorMessage);
      }
      throw error;
    }
  };
};

// Create a version that returns errors as objects instead of throwing
// Use this ONLY for components that don't use try-catch with dispatch
export const createCustomDispatchReturnErrors = (dispatch) => {
  return async (actionCreator) => {
    try {
      const actionResult = await dispatch(actionCreator);
      const result = unwrapResult(actionResult);

      return result;
    } catch (error) {
      // Return error object instead of throwing to prevent uncaught errors
      console.error('CustomDispatch returning error object:', error);
      
      if (error && typeof error === 'string') {
        return { error: error };
      } else if (error && error.message) {
        return { error: error.message };
      } else {
        return { error: 'An error occurred' };
      }
    }
  };
};
